import { unsafeCSS } from '@design.estate/dees-element';

/**
 * System UI font stack — the primary UI font for the design system.
 *
 * Uses the standard `system-ui` keyword so each platform renders its own native
 * UI font, with explicit fallbacks for older engines. No web font is bundled —
 * the native system stack is zero-weight and metrics-correct on each OS.
 */
export const systemFontFamily = `system-ui, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`;

/**
 * Monospace stack — the standard `ui-monospace` keyword renders the native
 * monospace UI font, with explicit fallbacks.
 */
export const monoFontFamily = `ui-monospace, 'Cascadia Mono', 'Roboto Mono', 'Courier New', monospace`;

/**
 * CSS-ready font family values.
 *
 * Prefer the `--dees-font-family` / `--dees-font-family-mono` CSS variables
 * (emitted by 00theme.ts) in component styles; these constants feed those tokens
 * and the ECharts theme (which cannot read CSS variables).
 */
export const cssSystemFontFamily = unsafeCSS(systemFontFamily);
export const cssMonoFontFamily = unsafeCSS(monoFontFamily);

/**
 * Base font styles applied to components.
 *
 * The system stack does not use Geist's OpenType character-variant features
 * (`cv05`/`cv11`), so only font smoothing is set here.
 */
export const baseFontStyles = unsafeCSS(`
  font-family: ${systemFontFamily};
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`);

// ============================================
// Deprecated aliases (theme migration)
// ============================================
// Geist / Cal Sans / Roboto Slab were the previous fonts. They now alias the
// system stack so existing importers keep working while components migrate to
// the `--dees-font-family` token. Remove these once no importer remains.

/** @deprecated use the system stack via `--dees-font-family`. */
export const geistSansFont = 'Geist Sans';
/** @deprecated alias of {@link systemFontFamily}. */
export const geistFontFamily = systemFontFamily;
/** @deprecated alias of {@link cssSystemFontFamily}. */
export const cssGeistFontFamily = cssSystemFontFamily;

/** @deprecated retained for back-compat during migration. */
export const intelOneMonoFont = 'Intel One Mono';

/** @deprecated display headings now use the system stack. */
export const calSansFont = 'Cal Sans';
/** @deprecated alias of {@link systemFontFamily}. */
export const calSansFontFamily = systemFontFamily;
/** @deprecated alias of {@link cssSystemFontFamily}. */
export const cssCalSansFontFamily = cssSystemFontFamily;

/** @deprecated serif content uses a native serif stack. */
export const robotoSlabFont = 'Roboto Slab';
/** @deprecated native serif stack. */
export const robotoSlabFontFamily = `Georgia, 'Times New Roman', serif`;
/** @deprecated CSS-ready native serif stack. */
export const cssRobotoSlabFontFamily = unsafeCSS(robotoSlabFontFamily);
